import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { UploadFile } from "../utils/functions";
import { useAuth } from "../hooks/useAuth";
import { PhotoIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";

type UploadableFile = {
  file: File;
  preview: string;
  progress: number;
  status: "uploading" | "success" | "error";
  signedId?: string;
};

type UploadProps = {
  multiple?: boolean;
  onUpload: (signedIds: string[]) => void;
  label?: string;
};

export function Upload({ multiple = true, onUpload, label }: UploadProps) {
  const { token } = useAuth();
  const [files, setFiles] = useState<UploadableFile[]>([]);

  // Run tasks with a limited concurrency to avoid overwhelming network/backends
  // Reason: Large multi-file uploads (20+) should not fire all requests at once
  const runWithConcurrency = async <T,>(tasks: Array<() => Promise<T>>, concurrency = 4): Promise<T[]> => {
    const results: T[] = new Array(tasks.length);
    let nextIndex = 0;

    const worker = async () => {
      while (true) {
        const current = nextIndex;
        nextIndex += 1;
        if (current >= tasks.length) break;
        results[current] = await tasks[current]();
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, () => worker());
    await Promise.all(workers);
    return results;
  };

  const handleUpload = async (filesToUpload: UploadableFile[]) => {
    // Create per-file upload tasks
    const tasks = filesToUpload.map((uploadableFile) => async () => {
      try {
        const [signedId] = await UploadFile([uploadableFile.file], token, (progress) => {
          setFiles((prevFiles) =>
            prevFiles.map((f) => (f.file === uploadableFile.file ? { ...f, progress } : f))
          );
        });
        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.file === uploadableFile.file
              ? { ...f, status: "success" as const, signedId, progress: 100 }
              : f
          )
        );
        return signedId as string;
      } catch (_e) {
        setFiles((prevFiles) =>
          prevFiles.map((f) => (f.file === uploadableFile.file ? { ...f, status: "error" as const } : f))
        );
        return null as unknown as string; // normalize type; we'll filter later
      }
    });

    const results = await runWithConcurrency(tasks, 4);
    const successfulUploads = results.filter((id): id is string => Boolean(id));
    if (successfulUploads.length > 0) {
      onUpload(successfulUploads);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles: UploadableFile[] = acceptedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        progress: 0,
        status: "uploading",
      }));

      if (multiple) {
        setFiles(prev => [...prev, ...newFiles]);
        handleUpload(newFiles);
      } else {
        setFiles(newFiles);
        handleUpload(newFiles);
      }
    },
    [multiple, token]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    accept: { "image/*": [] },
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 dark:border-gray-700 px-4 py-8 sm:px-6 sm:py-10"
      >
        <div className="text-center">
          <PhotoIcon
            className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-300 dark:text-gray-600"
            aria-hidden="true"
          />
          <div className="mt-4 text-sm leading-6 text-gray-600 dark:text-gray-300">
            <label
              htmlFor="file-upload"
              className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500 dark:bg-gray-800 dark:text-indigo-400"
            >
              <span>{label || (multiple ? "Upload files" : "Upload a file")}</span>
              <input {...getInputProps()} id="file-upload" className="sr-only" />
            </label>
            <p className="pl-1">
              {isDragActive
                ? "Drop the files here ..."
                : `or drag 'n' drop ${multiple ? "files" : "a file"}`}
            </p>
          </div>
          <p className="text-xs leading-5 text-gray-600 dark:text-gray-300">
            PNG, JPG, GIF up to 10MB
          </p>
        </div>
      </div>
      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-4">
          {files.map((f, idx) => (
            <div key={idx} className="relative w-full h-32 rounded-md overflow-hidden">
              <img
                src={f.preview}
                alt={f.file.name}
                className="w-full h-full object-cover"
                onLoad={() => URL.revokeObjectURL(f.preview)} // free memory
              />
              {f.status === "uploading" && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 mx-4">
                    <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${f.progress}%` }}></div>
                  </div>
                </div>
              )}
              {f.status === "success" && (
                <div className="absolute inset-0 bg-green-500 bg-opacity-75 flex items-center justify-center">
                  <CheckCircleIcon className="w-10 h-10 text-white" />
                </div>
              )}
              {f.status === "error" && (
                <div className="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
                  <XCircleIcon className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
